/**
 * 带注记的内容显示组件
 * Word 批注模式：右侧批注栏 + 牵引线 + 滚动同步
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { marked, Renderer } from 'marked';
import mermaid from 'mermaid';
import { Annotation } from '../types';
import { annotationApi } from '../services/api';
import AnnotationDialog from './AnnotationDialog';
import EditorContextMenu from './EditorContextMenu';
import { TrashIcon } from '@heroicons/react/24/outline';

interface AnnotatedContentProps {
  content: string;
  outlineNodeId: number;
  projectId: string;
  className?: string;
  onContentChange?: (newContent: string) => void;
  isActive?: boolean;  // 是否为当前激活的章节（用于显示批注栏）
  onAnnotationsChange?: () => void;  // 批注变更时的回调
  allAnnotations?: Annotation[];  // 所有批注（由父组件传入）
  showAnnotationPanel?: boolean;  // 控制批注面板显示/隐藏
  onToggleAnnotationPanel?: (show: boolean) => void;  // 切换批注面板显示状态
}

// 批注卡片位置信息
interface AnnotationPosition {
  annotation: Annotation;
  markTop: number;      // 高亮标记的顶部位置（相对于文档顶部）
  markCenterY: number;  // 高亮标记的中心Y（相对于视口）
  cardTop: number;      // 批注卡片的顶部位置
}

// 连接线组件 - 简化版，使用纯 CSS 实现
const AnnotationLines: React.FC<{
  annotationPositions: AnnotationPosition[];
  contentRef: React.RefObject<HTMLDivElement | null>;
}> = ({ annotationPositions, contentRef }) => {
  return (
    <div className="absolute left-0 top-0 w-full h-full pointer-events-none overflow-visible">
      {annotationPositions.map(({ annotation, cardTop }) => {
        const mark = contentRef.current?.querySelector(`[data-annotation-id="${annotation.id}"]`);
        if (!mark) return null;
        
        return (
          <div
            key={annotation.id}
            className="absolute"
            style={{
              top: cardTop + 25,
              left: -20,
              width: 20,
              height: 2,
              background: 'linear-gradient(to right, transparent, #fbbf24)',
            }}
          >
            {/* 圆点标记 */}
            <div 
              className="absolute w-2 h-2 rounded-full bg-yellow-400"
              style={{ left: -4, top: -3 }}
            />
          </div>
        );
      })}
    </div>
  );
};

export const AnnotatedContent: React.FC<AnnotatedContentProps> = ({
  content,
  outlineNodeId,
  projectId,
  className = '',
  onContentChange,
  isActive = true,
  onAnnotationsChange,
  allAnnotations: externalAnnotations,
  showAnnotationPanel: externalShowPanel = true,
  onToggleAnnotationPanel,
}) => {
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAnnotationDialog, setShowAnnotationDialog] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectedRange, setSelectedRange] = useState<Range | null>(null);
  
  // 右键菜单状态
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [hasSelectedText, setHasSelectedText] = useState(false);
  
  // 批注栏状态 - 使用父组件传入的状态，或本地状态作为后备
  const [localShowPanel, setLocalShowPanel] = useState(true);
  const showAnnotationPanel = onToggleAnnotationPanel ? externalShowPanel : localShowPanel;
  const setShowAnnotationPanel = onToggleAnnotationPanel || setLocalShowPanel;
  const [annotationPositions, setAnnotationPositions] = useState<AnnotationPosition[]>([]);
  
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const savedSelectionRef = useRef<{ text: string; range: Range | null }>({ text: '', range: null });

  // 加载注记 - 优先使用父组件传入的批注
  useEffect(() => {
    const processAnnotations = async () => {
      if (externalAnnotations) {
        // 使用父组件传入的所有批注，过滤出当前章节的
        const currentAnnotations = externalAnnotations.filter(
          (ann: Annotation) => ann.outline_node_id === outlineNodeId
        );
        
        // 检查每个批注是否能在内容中找到匹配
        const validAnnotations: Annotation[] = [];
        const invalidAnnotationIds: number[] = [];
        
        for (const annotation of currentAnnotations) {
          const hasMarkTag = content.includes(`data-annotation-id="${annotation.id}"`);
          const hasSelectedText = content.includes(annotation.selected_text);
          
          if (hasMarkTag || hasSelectedText) {
            validAnnotations.push(annotation);
          } else {
            invalidAnnotationIds.push(annotation.id);
          }
        }
        
        // 删除无法匹配的批注
        if (invalidAnnotationIds.length > 0) {
          console.log(`清理 ${invalidAnnotationIds.length} 个无法匹配的批注`);
          for (const id of invalidAnnotationIds) {
            try {
              await annotationApi.delete(id);
            } catch (err) {
              console.error(`删除批注 ${id} 失败:`, err);
            }
          }
          onAnnotationsChange?.();
        }
        
        setAnnotations(validAnnotations);
      } else if (projectId && outlineNodeId) {
        // 备用：如果没有传入所有批注，则单独加载
        loadAnnotations();
      }
    };
    
    processAnnotations();
  }, [externalAnnotations, outlineNodeId, content]);

  const loadAnnotations = async () => {
    setLoading(true);
    try {
      const response = await annotationApi.getByNode(outlineNodeId);
      const loadedAnnotations = response.data || [];
      
      // 检查每个批注是否能在内容中找到匹配
      const validAnnotations: typeof loadedAnnotations = [];
      const invalidAnnotationIds: number[] = [];
      
      for (const annotation of loadedAnnotations) {
        // 检查内容中是否包含该批注的选中文本（或者已有标记标签）
        const hasMarkTag = content.includes(`data-annotation-id="${annotation.id}"`);
        const hasSelectedText = content.includes(annotation.selected_text);
        
        if (hasMarkTag || hasSelectedText) {
          validAnnotations.push(annotation);
        } else {
          // 无法匹配，记录待删除
          invalidAnnotationIds.push(annotation.id);
        }
      }
      
      // 删除无法匹配的批注
      if (invalidAnnotationIds.length > 0) {
        console.log(`清理 ${invalidAnnotationIds.length} 个无法匹配的批注`);
        for (const id of invalidAnnotationIds) {
          try {
            await annotationApi.delete(id);
          } catch (err) {
            console.error(`删除批注 ${id} 失败:`, err);
          }
        }
        onAnnotationsChange?.();  // 通知父组件刷新全局列表
      }
      
      setAnnotations(validAnnotations);
    } catch (err) {
      console.error('加载注记失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 计算批注位置
  const updateAnnotationPositions = useCallback(() => {
    if (!contentRef.current || !containerRef.current || annotations.length === 0) {
      setAnnotationPositions([]);
      return;
    }
  
    const containerRect = containerRef.current.getBoundingClientRect();
    const positions: AnnotationPosition[] = [];
      
    annotations.forEach(annotation => {
      const mark = contentRef.current?.querySelector(`[data-annotation-id=\"${annotation.id}\"]`);
      if (mark) {
        const markRect = mark.getBoundingClientRect();
        const markTop = markRect.top - containerRect.top + containerRef.current!.scrollTop;
        const markCenterY = markRect.top + markRect.height / 2;  // 相对于视口的中心Y
        positions.push({
          annotation,
          markTop,
          markCenterY,
          cardTop: markTop,
        });
      } else {
        // 即使找不到 DOM 元素，也添加批注（使用预估位置）
        // 这样批注卡片就不会消失
        const estimatedTop = positions.length > 0 ? positions[positions.length - 1].markTop + 100 : 100;
        positions.push({
          annotation,
          markTop: estimatedTop,
          markCenterY: estimatedTop + 10,
          cardTop: estimatedTop,
        });
      }
    });
  
    // 按位置排序
    positions.sort((a, b) => a.markTop - b.markTop);
  
    // 计算避免重叠的偏移量（基于文档位置）
    // 同时记录每个批注需要向下偏移多少来避免重叠
    const minGap = 80;
    let accumulatedOffset = 0;
    let prevDocTop = 0;
      
    positions.forEach((pos, index) => {
      if (index > 0) {
        // 当前批注的文档位置 + 之前累积的偏移
        const currentDocTop = pos.markTop + accumulatedOffset;
        const prevDocBottom = prevDocTop + minGap;
          
        if (currentDocTop < prevDocBottom) {
          // 需要额外偏移
          const extraOffset = prevDocBottom - currentDocTop;
          accumulatedOffset += extraOffset;
        }
      }
      // 记录当前批注的最终文档位置（包含偏移）
      prevDocTop = pos.markTop + accumulatedOffset;
      // 将偏移量存入 cardTop（用于连接线定位）
      pos.cardTop = prevDocTop;
    });
  
    setAnnotationPositions(positions);
  }, [annotations]);

  // 监听滚动和内容变化，更新批注位置
  useEffect(() => {
    updateAnnotationPositions();
    
    // 监听多个滚动源
    const handleScroll = () => {
      requestAnimationFrame(updateAnnotationPositions);
    };
    
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    
    // 也监听 window 和 document 的滚动
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', updateAnnotationPositions);
    
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', updateAnnotationPositions);
    };
  }, [updateAnnotationPositions]);

  // 渲染带注记的内容
  const renderAnnotatedContent = useCallback(() => {
    // 先处理分页符：替换为占位符，避免被marked当作HTML注释过滤
    const pageBreakPlaceholder = '{{PAGEBREAK_PLACEHOLDER}}';
    let processedContent = content.replace(/<!-- PAGE_BREAK -->/g, pageBreakPlaceholder);

    // 预处理：去除非代码块内的行首多余空格，防止 marked 将缩进行误判为代码块
    const lines = processedContent.split('\n');
    let inFencedCode = false;
    processedContent = lines.map(line => {
      if (line.trim().startsWith('```')) {
        inFencedCode = !inFencedCode;
        return line;
      }
      if (inFencedCode) return line;
      // 去除行首空格（保留列表符号前的合理空格）
      return line.replace(/^[ \t]{4,}/, '  ');
    }).join('\n');

    // 自定义 Renderer：在图片渲染时直接修正 /uploads/ 相对路径
    const API_BASE_URL = process.env.REACT_APP_API_URL || '';
    const renderer = new Renderer();
    const originalImage = renderer.image.bind(renderer);
    renderer.image = (token) => {
      const html = originalImage(token);
      if (API_BASE_URL) {
        return html.replace(
          /src="(\/uploads\/[^"]+)"/g,
          (_, path) => `src="${API_BASE_URL}${path}"`
        );
      }
      return html;
    };

    let result = marked(processedContent, { breaks: true, renderer }) as string;
    
    // marked处理后，将占位符替换为完整的分页符HTML
    result = result.replace(/\{\{PAGEBREAK_PLACEHOLDER\}\}/g, 
      '<div data-type="page-break" class="page-break-node"><div class="page-break-line"><span class="page-break-text">分页符</span></div></div>'
    );
    
    // 处理 Mermaid 代码块
    result = result.replace(/<pre><code class="language-mermaid">([\s\S]*?)<\/code><\/pre>/g, (match, code) => {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = code;
      const decodedCode = textarea.value.trim();
      return `<div class="mermaid-block" data-mermaid-code="${encodeURIComponent(decodedCode)}"></div>`;
    });

    // 智能标记标题型段落（不缩进）
    // 规则：1) 纯粗体段落  2) 以编号模式开头且较短的段落
    result = result.replace(/<p([^>]*)>((?:(?!<\/p>)[\s\S])*?)<\/p>/g, (match, attrs, innerHtml) => {
      // 已有 class 的跳过
      if (attrs.includes('class=')) return match;

      const plainText = innerHtml.replace(/<[^>]+>/g, '').trim();
      if (!plainText) return match;

      // 规则1：整段纯粗体
      const isAllBold = /^<strong>[\s\S]*<\/strong>$/.test(innerHtml.trim()) &&
                        !/[^<]*<\/strong>[^<]+/.test(innerHtml.trim()); // 确保strong外无文本

      // 规则2：以常见编号模式开头且较短（子标题特征）
      const headingNumberPattern = /^(?:[\u4e00\u4e8c\u4e09\u56db\u4e94\u516d\u4e03\u516b\u4e5d\u5341]+[\u3001\uff0e.]|\uff08[\u4e00\u4e8c\u4e09\u56db\u4e94\u516d\u4e03\u516b\u4e5d\u5341]+\uff09|\([\u4e00\u4e8c\u4e09\u56db\u4e94\u516d\u4e03\u516b\u4e5d\u5341]+\)|\d+[\u3001\uff0e.]\s*|\(\d+\)|\uff08\d+\uff09|\u7b2c[\u4e00\u4e8c\u4e09\u56db\u4e94\u516d\u4e03\u516b\u4e5d\u5341\d]+[\u7ae0\u8282\u90e8\u5206\u7bc7\u6761])/;
      const isNumberedHeading = headingNumberPattern.test(plainText) && plainText.length <= 50;

      // 规则3：称谓/抬头行（如"致：xx公司"、"尊敬的xx"等，类似信函开头）
      const salutationPattern = /^(?:\u81f4[\uff1a:]|\u5c0a\u656c\u7684|\u656c\u542f\u8005|\u6536\u4ef6\u4eba[\uff1a:]|\u53d1\u4ef6\u4eba[\uff1a:])/;
      const isSalutation = salutationPattern.test(plainText);

      if (isAllBold || isNumberedHeading || isSalutation) {
        return `<p${attrs} class="heading-paragraph">${innerHtml}</p>`;
      }
      return match;
    });
    
    if (!annotations.length) return result;
    
    // 检查是否已经包含注记标记
    const hasAnnotationMarks = /<span[^>]+class="annotation-mark"[^>]+data-annotation-id/.test(result);
    
    if (hasAnnotationMarks) {
      // 为已有标记添加样式
      result = result.replace(
        /<span class="annotation-mark" data-annotation-id="(\d+)">([^<]+)<\/span>/g,
        (match, annotationId, text) => {
          return `<span class="annotation-mark" data-annotation-id="${annotationId}" style="background-color: rgba(253, 224, 71, 0.6); border-bottom: 2px solid #fbbf24; cursor: pointer; border-radius: 2px;">${text}</span>`;
        }
      );
      return result;
    }
    
    // 为现有注记添加标记（兼容旧数据）
    const sortedAnnotations = [...annotations].sort(
      (a, b) => b.selected_text.length - a.selected_text.length
    );

    const placeholders: { [key: string]: { html: string; id: number } } = {};
    let placeholderIndex = 0;

    sortedAnnotations.forEach((annotation) => {
      const text = annotation.selected_text;
      const placeholder = `__ANNOTATION_${placeholderIndex}__`;
      placeholderIndex++;
      
      let matchIndex = result.indexOf(text);
      
      if (matchIndex === -1) {
        const anchorLength = Math.min(30, text.length);
        const anchor = text.substring(0, anchorLength);
        matchIndex = result.indexOf(anchor);
      }
      
      if (matchIndex !== -1) {
        const matchedText = result.substring(matchIndex, matchIndex + text.length);
        
        placeholders[placeholder] = {
          html: `<span class="annotation-mark" data-annotation-id="${annotation.id}" style="background-color: rgba(253, 224, 71, 0.6); border-bottom: 2px solid #fbbf24; cursor: pointer; border-radius: 2px;">${matchedText}</span>`,
          id: annotation.id,
        };
        result = result.replace(matchedText, placeholder);
      }
    });

    Object.entries(placeholders).forEach(([placeholder, { html }]) => {
      result = result.replace(new RegExp(placeholder, 'g'), html);
    });

    return result;
  }, [content, annotations]);

  // 渲染内容到 DOM
  useEffect(() => {
    if (!contentRef.current) return;
    const html = renderAnnotatedContent();
    contentRef.current.innerHTML = html;
    renderMermaidBlocks();
    
    // 延迟更新批注位置，等待 DOM 渲染完成
    setTimeout(updateAnnotationPositions, 100);
  }, [content, annotations, renderAnnotatedContent, updateAnnotationPositions]);
  
  // 渲染 Mermaid 图表
  const renderMermaidBlocks = useCallback(async () => {
    if (!contentRef.current) return;
    
    mermaid.initialize({
      startOnLoad: false,
      theme: 'default',
      securityLevel: 'loose',
      suppressErrorRendering: true,
      fontFamily: 'Microsoft YaHei, 微软雅黑, sans-serif',
    });
    
    const mermaidBlocks = contentRef.current.querySelectorAll('.mermaid-block:not([data-rendered])');
    
    if (mermaidBlocks.length === 0) return;
    
    mermaidBlocks.forEach(async (block, index) => {
      const code = block.getAttribute('data-mermaid-code');
      if (!code) return;
      
      try {
        const decodedCode = decodeURIComponent(code);
        const id = `mermaid-preview-${index}-${Date.now()}`;
        const { svg } = await mermaid.render(id, decodedCode);
        
        const container = document.createElement('div');
        container.className = 'mermaid-svg-container';
        container.innerHTML = svg;
        block.innerHTML = '';
        block.appendChild(container);
        block.setAttribute('data-rendered', 'true');
      } catch (err) {
        console.error('Mermaid 渲染失败:', err);
        block.innerHTML = `<div class="text-red-500 p-4 border border-red-200 rounded bg-red-50">
          <div class="font-medium">流程图渲染失败</div>
          <pre class="mt-2 text-xs overflow-auto">${decodeURIComponent(code)}</pre>
        </div>`;
        block.setAttribute('data-rendered', 'true');
      }
    });
  }, []);

  // 右键菜单处理
  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const handleContextMenu = (e: MouseEvent) => {
      if (!contentElement.contains(e.target as Node)) return;
      
      const selection = window.getSelection();
      const selectedText = selection?.toString().trim();
      const hasSelection = !!(selection && !selection.isCollapsed && selectedText);
      
      if (hasSelection && selectedText) {
        const range = selection.getRangeAt(0).cloneRange();
        savedSelectionRef.current = { text: selectedText, range };
        
        e.preventDefault();
        e.stopPropagation();
        setHasSelectedText(true);
        setContextMenuPosition({ x: e.clientX, y: e.clientY });
        setShowContextMenu(true);
      }
    };

    document.addEventListener('contextmenu', handleContextMenu, true);
    return () => document.removeEventListener('contextmenu', handleContextMenu, true);
  }, []);

  // 添加注记
  const handleAddAnnotation = () => {
    const selection = window.getSelection();
    let text = '';
    let range: Range | null = null;
    
    if (selection && !selection.isCollapsed) {
      text = selection.toString().trim();
      range = selection.getRangeAt(0).cloneRange();
    } else if (savedSelectionRef.current.text) {
      text = savedSelectionRef.current.text;
      range = savedSelectionRef.current.range;
    }
    
    if (text) {
      setSelectedText(text);
      setSelectedRange(range);
      setShowAnnotationDialog(true);
    }
    
    savedSelectionRef.current = { text: '', range: null };
    setShowContextMenu(false);
  };

  // 提交注记
  const handleSubmitAnnotation = async (annotationContent: string) => {
    if (!projectId) {
      alert('请先保存项目后再添加注记');
      return;
    }
    
    try {
      const annotationData = {
        outline_node_id: outlineNodeId,
        selected_text: selectedText,
        content: annotationContent,
        position_data: selectedRange ? {
          startOffset: selectedRange.startOffset,
          endOffset: selectedRange.endOffset,
        } : undefined,
      };
      
      const response = await annotationApi.create(projectId, annotationData);
      const annotationId = response.data.id;
      
      if (onContentChange && selectedText) {
        const annotationTag = `<span class="annotation-mark" data-annotation-id="${annotationId}">${selectedText}</span>`;
        const index = content.indexOf(selectedText);
        if (index !== -1) {
          const newContent = content.substring(0, index) + annotationTag + content.substring(index + selectedText.length);
          onContentChange(newContent);
          
          if (contentRef.current && selectedRange) {
            try {
              const markHTML = `<span class="annotation-mark" data-annotation-id="${annotationId}" style="background-color: rgba(253, 224, 71, 0.6); border-bottom: 2px solid #fbbf24; cursor: pointer; border-radius: 2px;">${selectedText}</span>`;
              const range = selectedRange.cloneRange();
              range.deleteContents();
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = markHTML;
              range.insertNode(tempDiv.firstChild!);
            } catch (e) {
              console.error('直接插入DOM失败:', e);
            }
          }
        }
      }
      
      await loadAnnotations();
      onAnnotationsChange?.();  // 通知父组件批注已变更
      setShowAnnotationDialog(false);
      setSelectedText('');
      setSelectedRange(null);
      setShowAnnotationPanel(true);  // 添加注记后显示批注栏
    } catch (err: any) {
      console.error('创建注记失败:', err);
      alert('创建注记失败，请检查网络连接');
    }
  };

  // 删除注记
  const handleDeleteAnnotation = async (id: number) => {
    try {
      await annotationApi.delete(id);
      
      if (contentRef.current) {
        const annotationMark = contentRef.current.querySelector(`[data-annotation-id="${id}"]`);
        if (annotationMark) {
          const textContent = annotationMark.textContent || '';
          const textNode = document.createTextNode(textContent);
          annotationMark.parentNode?.replaceChild(textNode, annotationMark);
        }
      }
      
      if (onContentChange && content) {
        const updatedContent = content.replace(
          new RegExp(`<span[^>]*class="annotation-mark"[^>]*data-annotation-id="${id}"[^>]*>([^<]*)<\/span>`, 'g'),
          '$1'
        );
        const cleanedContent = updatedContent.replace(
          new RegExp(`<span[^>]*class="annotation-mark"[^>]*data-annotation-id="${id}"[^>]*>([\\s\\S]*?)<span[^>]*><\/span><\/span>`, 'g'),
          '$1'
        );
        onContentChange(cleanedContent);
      }
      
      await loadAnnotations();
      onAnnotationsChange?.();  // 通知父组件批注已变更
    } catch (err) {
      console.error('删除注记失败:', err);
    }
  };

  // 点击批注高亮对应文本
  const scrollToAnnotation = (annotationId: number) => {
    const mark = contentRef.current?.querySelector(`[data-annotation-id="${annotationId}"]`);
    if (mark) {
      mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // 闪烁效果
      mark.classList.add('annotation-flash');
      setTimeout(() => mark.classList.remove('annotation-flash'), 1000);
    }
  };

  // 点击内容区域中的批注高亮文本
  const handleContentClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('annotation-mark') || target.closest('.annotation-mark')) {
      const markElement = target.classList.contains('annotation-mark') ? target : target.closest('.annotation-mark');
      const annotationId = markElement?.getAttribute('data-annotation-id');
      if (annotationId) {
        // 打开批注面板
        setShowAnnotationPanel(true);
        // 滚动到对应批注
        setTimeout(() => scrollToAnnotation(parseInt(annotationId)), 100);
      }
    }
  };

  const hasAnnotations = annotations.length > 0;

  return (
    <div 
      ref={containerRef}
      className="relative flex"
      style={{ minHeight: '200px' }}
    >
      {/* 主内容区 */}
      <div className="flex-1">
        <div 
          ref={contentRef}
          className={`annotated-content ${className}`}
          style={{ 
            userSelect: 'text',
            maxWidth: '794px',
            width: '100%',
            margin: '0 auto',
            padding: '0 calc(2.54cm + 14px)',
          }}
          onClick={handleContentClick}
        />
      </div>

      {/* 批注面板开关按钮已移到 ContentEdit 右侧 box */}

      {/* 连接线层 - 使用 fixed 定位跨越内容区和右侧面板 */}
      {showAnnotationPanel && hasAnnotations && (
        <svg 
          className="fixed inset-0 w-full h-full pointer-events-none" 
          style={{ zIndex: 98 }}
        >
          {annotationPositions.map(({ annotation, markCenterY, markTop, cardTop }) => {
            // 获取高亮标记元素
            const mark = contentRef.current?.querySelector(`[data-annotation-id=\"${annotation.id}\"]`);
            if (!mark) return null;
            
            const markRect = mark.getBoundingClientRect();
            const markRight = markRect.right;
            const markBottom = markRect.bottom; // 使用底部位置
            
            // 获取右侧面板容器
            const annotationContainer = document.getElementById('annotation-list-container');
            if (!annotationContainer) return null;
            
            const containerRect = annotationContainer.getBoundingClientRect();
            
            // 计算避免重叠的偏移量
            const overlapOffset = cardTop - markTop;
            
            // 使用 markCenterY 计算卡片中心位置（跟随滚动）
            const cardMiddleY = markCenterY + overlapOffset;
            
            // 只有当高亮文本在视口内时才显示连接线
            if (markBottom < containerRect.top - 50 || markBottom > containerRect.bottom + 50) {
              return null;
            }
            
            // 连接线从高亮文本右下角到右侧批注卡片左侧中点
            const lineStartX = markRight;
            const lineEndX = containerRect.left; // 到面板左边缘
            
            // 计算卡片左侧中心位置
            const cardLeftX = containerRect.left + 4; // 卡片左边缘
            
            return (
              <g key={`line-${annotation.id}`}>
                {/* 从高亮文本底部到面板左边缘的主连接线 */}
                <path
                  d={`M ${lineStartX} ${markBottom} Q ${lineStartX + 30} ${markBottom}, ${(lineStartX + lineEndX) / 2} ${(markBottom + cardMiddleY) / 2} T ${lineEndX} ${cardMiddleY}`}
                  fill="none"
                  stroke="#fbbf24"
                  strokeWidth="1.5"
                  strokeDasharray="4 2"
                  opacity="0.6"
                />
                {/* 从面板左边缘到卡片的短连接线 */}
                <line
                  x1={lineEndX}
                  y1={cardMiddleY}
                  x2={cardLeftX}
                  y2={cardMiddleY}
                  stroke="#fbbf24"
                  strokeWidth="1.5"
                  strokeDasharray="2 2"
                  opacity="0.6"
                />
                {/* 高亮文本底部圆点 */}
                <circle
                  cx={lineStartX}
                  cy={markBottom}
                  r="3"
                  fill="#fbbf24"
                  opacity="0.8"
                />
                {/* 面板左边缘圆点 */}
                <circle
                  cx={lineEndX}
                  cy={cardMiddleY}
                  r="3"
                  fill="#fbbf24"
                  opacity="0.8"
                />
              </g>
            );
          })}
        </svg>
      )}

      {/* 右侧批注栏 - 使用 Portal 渲染到右侧面板 */}
      {showAnnotationPanel && hasAnnotations && (() => {
        const container = document.getElementById('annotation-list-container');
        if (!container) return null;
        
        // 获取右侧面板的位置信息
        const containerRect = container.getBoundingClientRect();
        
        return createPortal(
          <div className="absolute inset-0 pointer-events-auto overflow-hidden">
            {/* 批注卡片 - 使用 markCenterY 跟随滚动，加上偏移避免重叠 */}
            {annotationPositions.map(({ annotation, markCenterY, markTop, cardTop }) => {
              // 计算避免重叠的偏移量
              const overlapOffset = cardTop - markTop;
              
              // 计算批注卡片相对于右侧面板的位置
              // markCenterY 是视口位置，随滚动变化
              const adjustedTop = markCenterY - containerRect.top - 20 + overlapOffset;
              
              // 获取高亮标记元素
              const mark = contentRef.current?.querySelector(`[data-annotation-id=\"${annotation.id}\"]`);
              
              // 判断高亮文本是否在视口可见范围内
              const isVisible = mark && (() => {
                const markRect = mark.getBoundingClientRect();
                return markRect.bottom >= containerRect.top && markRect.top <= containerRect.bottom;
              })();
              
              // 如果高亮文本不在视口内，不显示批注卡片
              if (!isVisible) {
                return null;
              }
              
              return (
                <div
                  key={annotation.id}
                  className="absolute left-1 right-4 bg-white rounded-lg shadow-md border border-yellow-200 hover:shadow-lg transition-all duration-150 cursor-pointer group pointer-events-auto"
                  style={{
                    top: adjustedTop,
                    maxHeight: '80px',
                    zIndex: 100,
                  }}
                  onClick={() => scrollToAnnotation(annotation.id)}
                >
                  {/* 批注头部 */}
                  <div className="flex items-center justify-between px-3 py-1 bg-yellow-50 rounded-t-lg border-b border-yellow-100">
                    <span className="text-xs font-medium text-yellow-700 truncate flex-1">
                      {annotation.content?.substring(0, 12) || '批注'}...
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteAnnotation(annotation.id);
                      }}
                      className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                      title="删除批注"
                    >
                      <TrashIcon className="w-3 h-3" />
                    </button>
                  </div>
                  
                  {/* 批注内容 */}
                  <div className="p-2">
                    <div className="text-xs text-gray-700 line-clamp-2">
                      {annotation.content}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>,
          container
        );
      })()}
      
      {/* 样式 */}
      <style>{`
        .annotated-content .mermaid-block {
          margin: 1.5rem 0;
          padding: 1rem;
          background-color: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          overflow-x: auto;
        }
        .annotated-content .mermaid-block svg {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 0 auto;
        }
        /* 分页符样式 */
        .annotated-content .page-break-node {
          margin: 1.5rem 0;
          padding: 0;
          user-select: none;
        }
        .annotated-content .page-break-line {
          border-top: 2px dashed #9ca3af;
          position: relative;
          text-align: center;
          margin: 0.5rem 0;
        }
        .annotated-content .page-break-text {
          position: relative;
          top: -0.75rem;
          padding: 0 0.5rem;
          color: #6b7280;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.05em;
          background: white;
        }
        .annotation-flash {
          animation: flash 0.5s ease-in-out 2;
        }
        @keyframes flash {
          0%, 100% { background-color: rgba(253, 224, 71, 0.6); }
          50% { background-color: rgba(253, 224, 71, 1); }
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>

      {/* 右键菜单 */}
      <EditorContextMenu
        x={contextMenuPosition.x}
        y={contextMenuPosition.y}
        show={showContextMenu}
        onClose={() => setShowContextMenu(false)}
        onInsertMermaid={() => {}}
        onInsertTable={() => {}}
        hasSelectedText={hasSelectedText}
        onAddAnnotation={handleAddAnnotation}
        isPreviewMode={true}
      />

      {/* 添加注记对话框 */}
      <AnnotationDialog
        isOpen={showAnnotationDialog}
        onClose={() => {
          setShowAnnotationDialog(false);
          setSelectedText('');
          setSelectedRange(null);
        }}
        onSubmit={handleSubmitAnnotation}
        selectedText={selectedText}
      />
    </div>
  );
};

export default AnnotatedContent;
